import { type FieldPath, limit, type OrderByDirection, orderBy, type Query, type QueryConstraint, query, where } from 'firebase/firestore'

/**
 * Firestore のクエリを「論理和の上限」を破れない形で組み立てる。
 *
 * `in` / `not-in` / `array-contains-any` は**論理和**に展開される。2 つ並べると
 * 掛け算になり、Firestore の上限 30 を簡単に超える。
 *
 *     where('deviceId', 'in', [9 件]) + where('status', 'in', [5 件])
 *     → 45 disjunctions → FirebaseError: Too many disjunctions after normalization
 *
 * 実際にこれで買取のデバイス選択が丸ごと空になった。コメントで注意書きを
 * 置くのではなく、**型で通らないようにする**。
 *
 *   - `filter()` は演算子のリテラルを見て、論理和系なら `Disjunctive` を返す
 *   - `safeQuery()` は `Disjunctive` を **1 つまで**しか受け取れない
 *   - 生の `where()` は型が合わないので `safeQuery()` に渡せない
 *
 * 件数の上限 (30) は実行時にしか分からないので、`filter()` の中で見ている。
 *
 * 生の `query()` を直接使えば迂回できてしまうので、
 * `.grit/no-raw-firestore-disjunction.grit` で lint からも塞いでいる。
 */

/** 論理和に展開される演算子 */
export const DISJUNCTIVE_OPS = ['in', 'not-in', 'array-contains-any'] as const
export type DisjunctiveOp = (typeof DISJUNCTIVE_OPS)[number]

/** Firestore が 1 クエリで許す論理和の上限 */
export const MAX_DISJUNCTIONS = 30

type Simple = { readonly disjunctive: false; readonly constraint: QueryConstraint }
type Disjunctive = { readonly disjunctive: true; readonly constraint: QueryConstraint; readonly size: number }
export type SafeConstraint = Simple | Disjunctive

type SimpleOp = '<' | '<=' | '==' | '!=' | '>=' | '>' | 'array-contains'

/**
 * `where()` の代わり。演算子のリテラルで戻り値の型が変わる。
 * 論理和系は値が配列であることも型で要求する。
 */
export function filter(field: string | FieldPath, op: DisjunctiveOp, value: readonly unknown[]): Disjunctive
export function filter(field: string | FieldPath, op: SimpleOp, value: unknown): Simple
export function filter(field: string | FieldPath, op: DisjunctiveOp | SimpleOp, value: unknown): SafeConstraint {
  if ((DISJUNCTIVE_OPS as readonly string[]).includes(op)) {
    const values = value as readonly unknown[]
    if (values.length > MAX_DISJUNCTIONS) {
      throw new Error(`${op} に渡せるのは ${MAX_DISJUNCTIONS} 件まで (${String(field)} に ${values.length} 件)。呼び出し側で分割すること`)
    }
    return { disjunctive: true, constraint: where(field, op, values), size: values.length }
  }
  return { disjunctive: false, constraint: where(field, op as SimpleOp, value) }
}

export const sortBy = (field: string | FieldPath, direction: OrderByDirection = 'asc'): Simple => ({
  disjunctive: false,
  constraint: orderBy(field, direction),
})

export const take = (n: number): Simple => ({ disjunctive: false, constraint: limit(n) })

/**
 * 論理和系の制約が 2 つ以上あればコンパイルを通さない。
 * 2 つ目以降に当たった時点で `never` を返すので、その引数で型エラーになる。
 */
type AtMostOneDisjunctive<T extends readonly SafeConstraint[], Seen extends boolean = false> = T extends readonly [
  infer Head,
  ...infer Rest extends readonly SafeConstraint[],
]
  ? Head extends Disjunctive
    ? Seen extends true
      ? never
      : readonly [Head, ...AtMostOneDisjunctive<Rest, true>]
    : readonly [Head, ...AtMostOneDisjunctive<Rest, Seen>]
  : T

export const safeQuery = <T extends readonly SafeConstraint[]>(ref: Query, ...constraints: AtMostOneDisjunctive<T> & T): Query =>
  query(ref, ...(constraints as readonly SafeConstraint[]).map((c) => c.constraint))
